import { InviteService, type Invite } from "@/services/InviteService";
import { type Dispatch, type SetStateAction, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Trash2 } from "lucide-react";

interface InviteListProps {
    invites: Invite[];
    setInvites: Dispatch<SetStateAction<Invite[]>>;
    title: string;
    instructions: string
    showAcceptButton: boolean;
    onRemove: (invite: Invite) => Promise<void>;
}

const InviteList: React.FC<InviteListProps> = ({ invites, setInvites, title, instructions, showAcceptButton, onRemove }) => {
  const getInviteKey = (invite: Invite) => invite.id || invite.code || invite.email;
  const [cancelingInviteKeys, setCancelingInviteKeys] = useState<Set<string>>(new Set())
  const [acceptingInviteKeys, setAcceptingInviteKeys] = useState<Set<string>>(new Set())
  const isExpired = (date: string) => new Date(date) < new Date();
  const formatDate = (date: string) => new Date(date).toLocaleDateString();
    
  const handleCancelInvite = async (invite: Invite) => {
    const inviteKey = getInviteKey(invite);
    setCancelingInviteKeys((current) => new Set(current).add(inviteKey));
    try {
      await onRemove(invite);
      setInvites((currentInvites) => currentInvites.filter((currentInvite) => getInviteKey(currentInvite) !== inviteKey));
    } finally {
      setCancelingInviteKeys((current) => {
        const next = new Set(current);
        next.delete(inviteKey);
        return next;
      });
    }
  }

    const handleAcceptInvite = async (invite: Invite) => {
        const inviteKey = getInviteKey(invite);
        setAcceptingInviteKeys((current) => new Set(current).add(inviteKey));
        try {
            await InviteService.accept(invite.id);
            setInvites((currentInvites) => currentInvites.filter((currentInvite) => getInviteKey(currentInvite) !== inviteKey));
        } finally {
            setAcceptingInviteKeys((current) => {
                const next = new Set(current);
                next.delete(inviteKey);
                return next;
            });
        }
    }

    return (
        <> 
            <div className="mt-8 mb-4">
                <h2 className="text-xl font-bold text-gray-900">{title} </h2>
                <p className="text-sm text-gray-600">{instructions}</p>
            </div>        
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
                    <thead>
                    <tr className="bg-gray-50">
                        <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        From
                        </th>
                        <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        Code
                        </th>
                        <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        Identifier
                        </th>                        
                        <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        Invited On
                        </th>
                        <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        Expires
                        </th>
                        <th className="w-px whitespace-nowrap py-2 px-4 text-left text-sm font-semibold text-gray-700">
                        Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {invites.map((invite) => {
                        const inviteKey = getInviteKey(invite);
                        const isCancelingInvite = cancelingInviteKeys.has(inviteKey);
                        const isAcceptingInvite = acceptingInviteKeys.has(inviteKey);
                        const inviteExpired = isExpired(invite.expirationDate);

                        return (
                        <tr
                            key={inviteKey}
                            className="border-t border-gray-200 hover:bg-gray-50"
                        >
                            <td className="py-2 px-4 text-sm font-medium text-gray-900">
                            {invite.sentBy}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">
                            {invite.code}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">
                            {invite.userIdentifier}
                            </td>                            
                            <td className="py-2 px-4 text-sm text-gray-600">
                            {formatDate(invite.invitedOn)}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">
                            {inviteExpired ? (
                                <span className="font-medium text-red-600">Expired</span>
                            ) : (
                                formatDate(invite.expirationDate)
                            )}
                            </td>
                            <td className="w-px whitespace-nowrap py-2 px-4 text-sm text-white">
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        disabled={isCancelingInvite || isAcceptingInvite}
                                        onClick={() => handleCancelInvite(invite)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        {isCancelingInvite ? "Canceling..." : "Cancel"}
                                    </Button>
                                    {showAcceptButton && (
                                        <Button
                                            size="sm"
                                            disabled={isCancelingInvite || isAcceptingInvite || inviteExpired}
                                            onClick={() => handleAcceptInvite(invite)}
                                        >
                                            <Check className="h-4 w-4" />
                                            {isAcceptingInvite ? "Accepting..." : "Accept"}
                                        </Button>
                                    )}
                                </div>
                            </td>
                        </tr>
                        )
                    })}
                    {invites.length === 0 && (
                        <tr>
                            <td className="py-6 px-4 text-center text-sm text-gray-600" colSpan={6}>
                                No pending invites found.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </>
       );
}

export default InviteList;